/**
 * Epoch-ms formatting (docs/CONTRACT.md §2: every timestamp is integer epoch milliseconds).
 *
 * docs/DESIGN.md §7.9 decides which of these return an *isolated* value. A number that is formatted
 * as a compound — a duration (`4:15`), a numeric date (`12/10/26`), a decimal (`1.5`) — is a closed
 * unit and must be held apart from the sentence around it, because the separator inside it is a
 * neutral and a right-to-left paragraph will reorder the pair (the native POC renders `4:15` as
 * `١٥:٤`). Isolating it *here*, in the formatter, is stronger than isolating it at each call site:
 * the value is then safe in a JSX node, in a string interpolation and in a push-notification body
 * alike, and the rule cannot be forgotten by the next screen that formats a duration.
 *
 * The ones that are *not* isolated are listed with their reason in `formatPresence` and `formatDay`
 * below — a number followed by a word (`5 min ago`, `12 Oct 2026`) has a strong left-to-right
 * character between the runs, so it never trades places.
 */
import { isolateLtr } from './bidi'
import { currentLocale, t } from './i18n'

type Formatters = {
  time: Intl.DateTimeFormat
  day: Intl.DateTimeFormat
  weekday: Intl.DateTimeFormat
  stamp: Intl.DateTimeFormat
}

let cached: { locale: string; formatters: Formatters } | null = null

/**
 * The `Intl` formatters follow the *app's* locale, not the browser's: an Arabic chrome that said
 * `Mon` and `11:21 PM` would be half-translated, and `undefined` means "whatever the browser runs
 * in". Rebuilt when the locale moves, so a switch takes effect on the next render.
 */
function formatters(): Formatters {
  const locale = currentLocale()
  if (cached?.locale !== locale) {
    cached = {
      locale,
      formatters: {
        time: new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }),
        day: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }),
        weekday: new Intl.DateTimeFormat(locale, { weekday: 'short' }),
        stamp: new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric', year: '2-digit' }),
      },
    }
  }
  return cached.formatters
}

/** A clock time is `H:MM` — two number runs around a colon. Isolated. */
export function formatTime(at: number): string {
  return isolateLtr(formatters().time.format(at))
}

function startOfDay(at: number): number {
  const date = new Date(at)
  date.setHours(0, 0, 0, 0)
  return date.getTime()
}

/**
 * `Today`, `Yesterday`, a weekday inside the last week, else a full date. Not isolated: a day name
 * is a word, and `12 Oct 2026` has the month between its two numbers.
 */
export function formatDay(at: number): string {
  const today = startOfDay(Date.now())
  const day = startOfDay(at)
  const diffDays = Math.round((today - day) / 86_400_000)
  if (diffDays <= 0) return t('time.today')
  if (diffDays === 1) return t('time.yesterday')
  if (diffDays < 7) return formatters().weekday.format(at)
  return formatters().day.format(at)
}

/** Compact stamp for chat-list rows: time today, weekday this week, else a numeric date. */
export function formatListStamp(at: number): string {
  const today = startOfDay(Date.now())
  const day = startOfDay(at)
  const diffDays = Math.round((today - day) / 86_400_000)
  if (diffDays <= 0) return formatTime(at)
  if (diffDays < 7) return formatters().weekday.format(at)
  return isolateLtr(formatters().stamp.format(at))
}

/** `M:SS`. Isolated. */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return isolateLtr(`${minutes}:${String(seconds).padStart(2, '0')}`)
}

/**
 * `1.5 MB`. Isolated: the value is a decimal, and a decimal is two number runs around a point.
 * The unit is a word, but the value ships as one object either way.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  const digits = value >= 100 || unit === 0 ? 0 : 1
  return isolateLtr(`${value.toFixed(digits)} ${units[unit]}`)
}

/**
 * The Android watchdog alarm interval, e.g. `2 min 30 s`. Isolated: it is a duration built from two
 * numbers, which is the shape §7.9 names, even though the word between them is not itself a
 * separator. `45 s` and `5 min` come out of the same branch a plain number followed by a word, and
 * are isolated with it so the value is one object whatever the browser chose.
 */
export function formatInterval(ms: number): string {
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return isolateLtr(`${seconds} s`)
  if (seconds % 60 === 0) return isolateLtr(`${seconds / 60} min`)
  return isolateLtr(`${Math.floor(seconds / 60)} min ${seconds % 60} s`)
}

/**
 * `online`, `last seen 5 minutes ago`, … A translated sentence with one plain number in it — no
 * isolate: the number stands between words, so a right-to-left paragraph has nothing to reorder
 * (the chrome itself is not mirrored, §7.1).
 */
export function formatPresence(online: boolean, lastSeenAt: number | null): string {
  if (online) return t('presence.online')
  if (!lastSeenAt) return t('presence.offline')
  const seconds = Math.max(0, Math.round((Date.now() - lastSeenAt) / 1000))
  if (seconds < 60) return t('presence.lastSeenJustNow')
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return t('presence.lastSeenMinutes', { count: minutes })
  const hours = Math.round(minutes / 60)
  if (hours < 24) return t('presence.lastSeenHours', { count: hours })
  return t('presence.lastSeenDate', { date: formatters().day.format(lastSeenAt) })
}
